import { useCallback } from 'react';

import { TFunction, useTranslation } from '@lyearn/i18n';
import { useSnackbar } from '@lyearn/ui';
import { FormikHelpers } from '@/components/Form';
import { FilterGroupInput } from '@/components/Form/components/Conditions/types';
import { adaptFilterGroups } from '@/components/Form/components/Conditions/utils';
import useNavigation from '@/hooks/useNavigation';
import { ContentCollectionGroupType, CreateGroupInput, GroupTypeEnum } from '@/types/schema';
import { getErrorCode } from '@/utils/api/response';

import { getGroupDetailsRoute } from '../../../utils';
import { CreateUserGroupInput, GroupEditorProps } from '../types';
import useCreateAndUpdateGroup from './useCreateAndUpdateGroup';

const DUPLICATE_NAME_ERROR_CODE = 'DUPLICATE_GROUP_NAME';

const getSnackbarMessage = (t: TFunction, type: GroupTypeEnum, groupId?: string) => {
  if (type === GroupTypeEnum.UserCollection) {
    return groupId
      ? t('Group:Editor.GroupUpdatedSuccessfully')
      : t('Group:Editor.GroupCreatedSuccessfully');
  }

  return groupId
    ? t('Group:Editor.CollectionUpdatedSuccessfully')
    : t('Group:Editor.CollectionCreatedSuccessfully');
};

function useGroupAction(props: GroupEditorProps) {
  const { t } = useTranslation();
  const { groupId, type, onSubmit, onClose } = props;
  const { showSnackbar, showErrorSnackbar } = useSnackbar();
  const navigation = useNavigation();
  const {
    createGroup,
    updateGroup,
    fetchingGroup,
    createCollection,
    updateCollection,
    fetchingCollection,
  } = useCreateAndUpdateGroup();

  const _onSubmit = useCallback(
    async (values: CreateUserGroupInput, formikHelpers: FormikHelpers<CreateUserGroupInput>) => {
      let result;
      let id: string | undefined;

      const data = {
        ...values,
        filterGroup: adaptFilterGroups(values.filterGroup as FilterGroupInput),
      };

      // We have added default visibility for content collection.
      // User collection doesn't need it so removing it from the input.
      if (type === GroupTypeEnum.UserCollection) {
        (data as CreateGroupInput).contentVisibilitySettings = undefined;
      }

      if (groupId) {
        if (type === GroupTypeEnum.UserCollection) {
          result = await updateGroup({ data: { ...data, groupId } });
          id = groupId;
        } else {
          result = await updateCollection({ data: { ...data, groupId } });
          id = groupId;
        }
      } else {
        if (type === GroupTypeEnum.UserCollection) {
          result = await createGroup({ data });
          id = result.data?.createUserCollectionGroup.group.id;
        } else {
          result = await createCollection({ data: { ...data, type } });
          id = (result.data?.createGroup as ContentCollectionGroupType)?.id;
        }
      }

      if (!result || result.error) {
        if (getErrorCode(result.error) === DUPLICATE_NAME_ERROR_CODE) {
          formikHelpers.setFieldError(
            'name',
            type === GroupTypeEnum.UserCollection
              ? t('Group:Editor.GroupDuplicateError')
              : t('Group:Editor.CollectionDuplicateError'),
          );
        } else {
          showErrorSnackbar({ message: t('Common:Error.SomeErrorOccurredTryAgain') });
        }
      } else {
        showSnackbar({ message: getSnackbarMessage(t, type, groupId) });
        if (!onSubmit || !id) {
          if (id) {
            navigation.replace(getGroupDetailsRoute(type, id));
          }
          return;
        }
        onSubmit(id);
        onClose?.();
      }
    },
    [
      type,
      groupId,
      updateGroup,
      updateCollection,
      createGroup,
      createCollection,
      t,
      showErrorSnackbar,
      showSnackbar,
      onSubmit,
      onClose,
      navigation,
    ],
  );

  return {
    fetching: fetchingGroup || fetchingCollection,
    onSubmit: _onSubmit,
  };
}

export default useGroupAction;
